import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  itemsPerPage: number;
  currentItemsCount: number;
}

const ServerPagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  hasNextPage,
  hasPrevPage,
  itemsPerPage,
  currentItemsCount,
}) => {
  const handlePrevPage = () => {
    if (hasPrevPage) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    onPageChange(page);
  };

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    // Adjust startPage if we're near the end
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = getPageNumbers();
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = startItem + currentItemsCount - 1;

  return (
    <div className="flex flex-col items-center mb-5 md:flex-row">
      <div className="mb-4 grow md:mb-0">
        <p className="text-slate-500 dark:text-zink-200">
          Showing <b>{startItem}</b> to <b>{endItem}</b> of <b>{totalItems}</b> Results
        </p>
      </div>
      <ul className="flex flex-wrap items-center gap-2 shrink-0">
        {/* Previous Button */}
        <li>
          <button
            onClick={handlePrevPage}
            disabled={!hasPrevPage}
            className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500 ${
              !hasPrevPage 
                ? 'text-slate-400 dark:text-zink-300 cursor-not-allowed opacity-50' 
                : 'hover:text-custom-500 dark:hover:text-custom-500'
            }`}
          >
            <ChevronLeft className="size-4 mr-1 rtl:rotate-180" /> Prev
          </button>
        </li>

        {/* Page Numbers */}
        {pageNumbers.map((page) => (
          <li key={page}>
            <button
              onClick={() => handlePageClick(page)}
              className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 ${
                currentPage === page
                  ? 'text-white dark:text-white bg-custom-500 dark:bg-custom-500 border-custom-500 dark:border-custom-500'
                  : 'text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500'
              }`}
            >
              {page}
            </button>
          </li>
        ))}

        {/* Next Button */}
        <li>
          <button
            onClick={handleNextPage}
            disabled={!hasNextPage}
            className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 h-8 px-3 transition-all duration-150 ease-linear border rounded border-slate-200 dark:border-zink-500 text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-100 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500 ${
              !hasNextPage 
                ? 'text-slate-400 dark:text-zink-300 cursor-not-allowed opacity-50' 
                : 'hover:text-custom-500 dark:hover:text-custom-500'
            }`}
          >
            Next <ChevronRight className="size-4 ml-1 rtl:rotate-180" />
          </button>
        </li>
      </ul>
    </div>
  );
};

export default ServerPagination;